import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

export type CurrencyUnit = "None" | "Tkr" | "Mkr";

interface CurrencyContextValue {
    unit: CurrencyUnit;
    setUnit: (u: CurrencyUnit) => void;
    divider: number;
    formatNumber: (value: number) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | undefined>(undefined);

export function CurrencyProvider({ children }: { children: React.ReactNode }) {
    const [unit, setUnit] = useState<CurrencyUnit>('Tkr');

    const divider = useMemo(() => {
        switch (unit) {
            case 'Tkr':
                return 1_000;
            case 'Mkr':
                return 1_000_000;
            default:
                return 1;
        }
    }, [unit]);

    const formatNumber = useCallback((value: number) => {
        const scaled = value / divider;
        if (scaled === 0) return '0';

        const absScaled = Math.abs(scaled);
        let decimals = 0;
        if (absScaled < 1) {
            decimals = Math.ceil(-Math.log10(absScaled));
        }

        const str = scaled.toFixed(decimals);
        const [intPart, decPart] = str.split('.');
        const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return decPart ? `${withSpaces}.${decPart}` : withSpaces;
    }, [divider]);

    return (
        <CurrencyContext.Provider value={{ unit, setUnit, divider, formatNumber }}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        throw new Error('useCurrency must be inside CurrencyProvider');
    }
    return ctx;
}